import Vue from 'vue';
import UserBuilder from '@/services/user-builder.service';
import api from '@/api';
import { REMOTE_PATHS } from '@/constants/remote-paths.constant';
import { modals } from '@/constants/modals.constant';
import router from '@/router';

const state = {
  currentUser: null,
  userBuilder: new UserBuilder(),
  history: [],
  historyNextToken: null,
  usersInfo: {},
  usersInfoQueue: {},
  contacts: [],
  subscription: null,
  currentUserInfo: null
};

const getters = {
  subscription(state) {
    return state.subscription || {};
  },
  contacts(state) {
    return state.contacts;
  },

  currentUser(state) {
    return state.currentUser || {};
  },

  usersInfo(state) {
    return state.usersInfo;
  },

  currentUsersInfo(state) {
    return state.currentUserInfo || {};
  },

  currentUsersAvatarUrl(state) {
    return state.currentUserInfo ? state.currentUserInfo.avatarUrl : null;
  },

  userHistory(state) {
    const groups = {};
    const key = 'group';
    state.history.forEach(v => {
      v.group = Vue.moment(v.startTimeInMillis).format('MMMM');
      const durationMillis = v.endTimeInMillis - v.startTimeInMillis;
      if (durationMillis >= 3600000) {
        v.duration = Vue.moment(durationMillis).format('hh[hrs] mm[min ]ss[sec]');
      } else if (durationMillis >= 60000) {
        v.duration = Vue.moment(durationMillis).format('mm[min ]ss[sec]');
      } else {
        v.duration = Vue.moment(durationMillis).format('ss[sec]');
      }
      const cell = v[key];
      if (!groups.hasOwnProperty(cell)) {
        groups[cell] = [];
      }
      groups[cell].push(v);
    });
    return {
      groups,
      length: state.history.length
    };
  }
};

const actions = {
  checkSubscription({ state, commit, dispatch }) {
    api.checkUserSubscription().then(response => {
      const decoded = state.userBuilder.decodeBuyResponse(response.body);
      state.subscription = decoded.subscription;
      state.subscription.expireTimeMillis = new Date(decoded.subscription.expireTimeMillis);
    });
  },

  setConferenceUrl(
    { state, commit, dispatch },
    username
  ) {
    const body = state.userBuilder.createPutUsernameRequestBody(username);
    api.setUsername(body).then(response => {
      const decoded = state.userBuilder.decodePutUsernameResponse(response);
      state.currentUser = decoded.currentUser;
      localStorage.setItem('currentUser', JSON.stringify(decoded.currentUser));
    });
  },

  setConferencePin(
    { state, commit, dispatch },
    pin
  ) {
    const body = state.userBuilder.createPutConferencePinRequestBody(pin);
    api.setUserPin(body).then(response => {
      const decoded = state.userBuilder.decodePutConferencePinResponse(response.data);
      state.currentUser = decoded.currentUser;
      localStorage.setItem('currentUser', JSON.stringify(decoded.currentUser));
    });
  },

  getUserHistory({ state, commit, dispatch }) {
    return new Promise((resolve, reject) => {
      const params = {items: 10};
      if (state.historyNextToken) {
        params.next_token = state.historyNextToken;
      } else {
        state.history = [];
      }
      api.getHistory(params).then(response => {
        const decoded = state.userBuilder.decodeHistoryResponse(response.data);
        decoded.entries.forEach(conf => {
          conf.attendees = [];
          conf.attendeesUserIds.forEach(id => {
            dispatch('getUserInfo', id).then(user => {
              conf.attendees.push(user.name);
            });
          });
        });
        state.history = state.history.concat(decoded.entries);
        state.historyNextToken = decoded.nextToken;
        resolve(!decoded.nextToken);
      });
    });
  },

  getUserContacts({ state, commit, dispatch }) {
    return new Promise((resolve, reject) => {
      api.getContacts().then(response => {
        const decoded = state.userBuilder.decodeContactsResponse(response.data);
        state.contacts = [];
        decoded.contacts.forEach(contact => {
          dispatch('getUserInfo', contact.userId).then(user => {
            contact.username = user.username;
            contact.avatarUrl = user.avatarUrl;
            state.contacts.push(contact);
          });
        });
      }, err => {
        reject(err);
      });
    });
  },

  getUserInfo({ state, commit, dispatch }, id) {
    let userPromise;
    if (state.usersInfoQueue[id]) {
      userPromise = state.usersInfoQueue[id];
    } else {
      userPromise = new Promise((resolve, reject) => {
        if (!state.usersInfo[id]) {
          if (id === 'unavailable') {
            resolve();
          } else {
            state.usersInfoQueue[id] = userPromise;
            api.getUserInfo(id).then(response => {
              const decoded = state.userBuilder.decodeUserResponse(response.data);
              state.usersInfo[id] = decoded.user;
              resolve(decoded.user);
            });
          }
        } else {
          resolve(state.usersInfo[id]);
        }
      });
    }
    return userPromise;
  },

  getCurrentUser({ state, commit, dispatch }) {
    api.getCurrentUser().then(response => {
      const decodedResponse = state.userBuilder.decodeCurrentUserResponse(response.data);
      localStorage.setItem(
        'currentUser',
        JSON.stringify(decodedResponse.currentUser)
      );
      dispatch('getUserInfo', decodedResponse.currentUser.userId).then(user => {
        commit('setCurrentUserInfo', user);
      });
      commit('setCurrentUser', decodedResponse.currentUser);
      if (
        router.history.current.name === 'MainMenu' ||
        router.history.current.name === 'home-tab'
      ) {
        dispatch('connectCallChannelSocket');
        router.push({
          name: 'MainMenu',
          params: {username: decodedResponse.currentUser.username}
        });
      }
    });
  },

  logout({ state, commit, dispatch}) {
    Vue.prototype.$cookie.delete('accessToken', {domain: REMOTE_PATHS.COOKIE_DOMAIN});
    Vue.prototype.$cookie.delete('refreshToken', {domain: REMOTE_PATHS.COOKIE_DOMAIN});
    window.location.href = `${REMOTE_PATHS.AUTH_PATH}`;
  }
};

const mutations = {
  clearHistoryState(state) {
    state.history = [];
    state.historyNextToken = null;
  },

  setCurrentUser(state, user) {
    state.currentUser = user;
  },

  setCurrentUserInfo(state, user) {
    state.currentUserInfo = user;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/user/user.js